import React, { FormEvent, useState, useEffect } from 'react';
import { Head, Link, useForm } from '@inertiajs/react';
import DashboardLayout from '../../../components/DashboardLayout';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Card, CardContent } from '../../../components/ui/card';

interface NewProps {
  errors?: string[];
  import?: any;
  import_started?: boolean;
}

const New: React.FC<NewProps> = ({ errors = [], import: importData, import_started = false }) => {
  const [fileType, setFileType] = useState<'tsv' | 'json' | null>(null);
  const [startIndex, setStartIndex] = useState('');
  const [endIndex, setEndIndex] = useState('');
  const [indexErrors, setIndexErrors] = useState<{start?: string, end?: string}>({});

  const { data, setData, post, processing, errors: formErrors } = useForm({
    geoname_import: {
      file: null as File | null,
      format: 'tsv' as 'tsv' | 'json',
      data: {} as any
    }
  });

  const detectFileType = async (file: File): Promise<'tsv' | 'json'> => {
    // Check extension first
    if (file.name.toLowerCase().endsWith('.json')) {
      return 'json';
    }
    if (file.name.toLowerCase().endsWith('.tsv') || file.name.toLowerCase().endsWith('.txt')) {
      return 'tsv';
    }

    // Check content
    try {
      const text = await file.slice(0, 100).text();
      if (text.trim().startsWith('{') || text.trim().startsWith('[')) {
        return 'json';
      }
    } catch (e) {
      // Ignore
    }

    return 'tsv'; // Default
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setData('geoname_import.file', file);

    if (file) {
      const type = await detectFileType(file);
      setFileType(type);
      setData('geoname_import.format', type);
    } else {
      setFileType(null);
      setData('geoname_import.format', 'tsv');
    }
  };

  const validateIndices = () => {
    const errors: {start?: string, end?: string} = {};
    const start = parseInt(startIndex);
    const end = endIndex ? parseInt(endIndex) : null;

    if (startIndex && (isNaN(start) || start < 0)) {
      errors.start = 'Start index must be a non-negative integer';
    }

    if (endIndex && (isNaN(end!) || end! < 0)) {
      errors.end = 'End index must be a non-negative integer';
    }

    if (startIndex && endIndex && start >= end!) {
      errors.end = 'End index must be greater than start index';
    }

    setIndexErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleIndexChange = (field: 'start' | 'end', value: string) => {
    if (field === 'start') {
      setStartIndex(value);
    } else {
      setEndIndex(value);
    }
    // Clear errors on change
    setIndexErrors(prev => ({ ...prev, [field]: undefined }));
  };

  useEffect(() => {
    const dataObj: any = {};
    if (startIndex) dataObj.start_index = parseInt(startIndex);
    if (endIndex) dataObj.end_index = parseInt(endIndex);
    setData('geoname_import.data', dataObj);
  }, [startIndex, endIndex, setData]);

  // Poll for import status
  useEffect(() => {
    if (import_started && importData?.id) {
      const pollStatus = async () => {
        try {
          const response = await fetch(`/admin/geonames/${importData.id}.json`, {
            headers: {
              'X-Requested-With': 'XMLHttpRequest'
            }
          });
          if (response.ok) {
            const data = await response.json();
            // For simplicity, redirect when done
            if (data.status === 'completed' || data.status === 'failed') {
              window.location.href = '/admin/geonames';
            }
          }
        } catch (e) {
          // Ignore
        }
      };

      const interval = setInterval(pollStatus, 2000); // Poll every 2 seconds
      return () => clearInterval(interval);
    }
  }, [import_started, importData?.id]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (fileType === 'json' && !validateIndices()) {
      return;
    }
    post('/admin/geonames');
  }

  return (
    <DashboardLayout>
      <Head title="New Geoname Import" />

      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">New Geoname Import</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Upload a file to import geoname data.
            </p>
          </div>
          <Link href="/admin/geonames">
            <Button variant="outline">Back to List</Button>
          </Link>
        </div>

        {/* Errors */}
        {errors.length > 0 && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
            <ul className="list-disc list-inside">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Form */}
        <Card>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} encType="multipart/form-data" className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="file">Upload File</Label>
              <Input
                id="file"
                name="geoname_import[file]"
                type="file"
                onChange={handleFileChange}
                required
              />
              {formErrors['geoname_import.file'] && (
                <p className="text-sm text-red-600">{formErrors['geoname_import.file']}</p>
              )}
              {fileType && (
                <p className="text-sm text-muted-foreground">Detected format: {fileType.toUpperCase()}</p>
              )}
            </div>

            {fileType === 'json' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_index">Start Index (optional)</Label>
                  <Input
                    id="start_index"
                    type="number"
                    min="0"
                    value={startIndex}
                    onChange={(e) => handleIndexChange('start', e.target.value)}
                    placeholder="0"
                  />
                  {indexErrors.start && (
                    <p className="text-sm text-red-600">{indexErrors.start}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_index">End Index (optional)</Label>
                  <Input
                    id="end_index"
                    type="number"
                    min="0"
                    value={endIndex}
                    onChange={(e) => handleIndexChange('end', e.target.value)}
                    placeholder="Leave empty for all"
                  />
                  {indexErrors.end && (
                    <p className="text-sm text-red-600">{indexErrors.end}</p>
                  )}
                </div>
              </div>
            )}

            {import_started && importData && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Import Progress</Label>
                  <div className="w-full bg-muted rounded-full h-2.5">
                    <div
                      className="bg-primary h-2.5 rounded-full transition-all duration-300"
                      style={{ width: importData.status === 'completed' ? '100%' : importData.status === 'processing' ? '50%' : '0%' }}
                    ></div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Status: {importData.status} {importData.statistics?.processed_count ? `(${importData.statistics.processed_count} processed)` : ''}
                  </p>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <Link href="/admin/geonames">
                <Button type="button" variant="outline">Cancel</Button>
              </Link>
              <Button type="submit" disabled={processing}>
                {processing ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default New;